import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { CrossAppMessagingService } from 'src/app/core/_services/crossApp-messaging.service';
import { isNativeShell } from 'src/app/core/_helpers/native-shell';

/**
 * Prijava/odjava slanja GPS pozicije vozača.
 * - Native shell: prosleđuje native-u (`deliveryLocation.start/stop`) koji šalje poziciju i u
 *   POZADINI dok se vozač ne odjavi (foreground servis). Ovo je glavni put.
 * - Čist web (dev): fallback na `navigator.geolocation.watchPosition` — radi SAMO dok je tab otvoren
 *   (bez pozadine); POST na isti endpoint.
 */
@Injectable({ providedIn: 'root' })
export class DeliveryLocationService {
  private activeCompanyID: number | null = null;
  private webWatchId: number | null = null;
  private lastWebSent: { lat: number; lng: number } | null = null;

  constructor(
    private crossApp: CrossAppMessagingService,
    private http: HttpClient
  ) { }

  /** Počni slanje pozicije za datu firmu. Idempotentno. */
  start(companyID: number): void {
    if (this.activeCompanyID === companyID) {
      return;
    }
    this.activeCompanyID = companyID;

    if (isNativeShell()) {
      this.crossApp.sendRawMessage({
        action: 'deliveryLocation.start',
        data: JSON.stringify({ companyID })
      });
    } else {
      this.startWeb(companyID);
    }
  }

  /** Zaustavi slanje (logout). */
  stop(): void {
    if (this.activeCompanyID === null) {
      return;
    }
    this.activeCompanyID = null;

    if (isNativeShell()) {
      this.crossApp.sendRawMessage({ action: 'deliveryLocation.stop' });
    } else {
      this.stopWeb();
    }
  }

  private startWeb(companyID: number): void {
    if (this.webWatchId !== null || !('geolocation' in navigator)) {
      return;
    }
    this.webWatchId = navigator.geolocation.watchPosition(
      pos => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        // Throttle ~25m da ne šaljemo na svaki mali pomak.
        if (this.lastWebSent && this.distanceMeters(this.lastWebSent, { lat, lng }) < 25) {
          return;
        }
        this.lastWebSent = { lat, lng };
        this.http.post(environment.apiBase + 'order/ReportLocation', {
          companyID,
          lat,
          lng,
          accuracy: pos.coords.accuracy,
          heading: pos.coords.heading,
          speed: pos.coords.speed,
          recordedUtc: new Date().toISOString()
        }).subscribe({ error: () => { /* fire-and-forget */ } });
      },
      () => { /* korisnik odbio / greška */ },
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 }
    );
  }

  private stopWeb(): void {
    if (this.webWatchId !== null) {
      navigator.geolocation.clearWatch(this.webWatchId);
      this.webWatchId = null;
    }
    this.lastWebSent = null;
  }

  private distanceMeters(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
    const R = 6371000;
    const dLat = (b.lat - a.lat) * Math.PI / 180;
    const dLng = (b.lng - a.lng) * Math.PI / 180;
    const s = Math.sin(dLat / 2) ** 2 +
      Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(s));
  }
}
